import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import { DCard, BtnGhost } from '../components/MarketplaceAtoms.js';
import { profileGroovesBalance } from '../constants/groovesEconomy.js';

const btnMini = {
  padding: '5px 10px',
  borderRadius: 8,
  border: '0.5px solid rgba(200,168,75,0.35)',
  background: 'rgba(200,168,75,0.08)',
  color: C.gold,
  fontSize: 10,
  fontWeight: 600,
  cursor: 'pointer',
};

const STAT_LABELS = [
  ['listings', 'Anuncios marketplace'],
  ['coleccion', 'Discos en colección'],
  ['comunidad', 'Posts Comunidad'],
  ['guardados_comunidad', 'Guardados Comunidad'],
  ['reviews_out', 'Valoraciones dadas'],
  ['reviews_in', 'Valoraciones recibidas'],
  ['wishlist', 'Wishlist'],
  ['pujas', 'Pujas'],
  ['compras', 'Compras'],
  ['ventas', 'Ventas'],
  ['seguidores', 'Seguidores'],
  ['siguiendo', 'Siguiendo'],
  ['notif', 'Notificaciones'],
  ['me_gusta_noticias', 'Me gusta noticias'],
  ['mensajes', 'Mensajes enviados'],
];

function formatFecha(ts) {
  if (!ts) return '—';
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(ts));
  } catch {
    return '—';
  }
}

async function countExact(table, eqCol, uid) {
  const { count, error } = await supabase.from(table).select('*', { count: 'exact', head: true }).eq(eqCol, uid);
  if (error) return null;
  return typeof count === 'number' ? count : 0;
}

/**
 * Lista de usuarios + ficha estadísticas y moderación (`account_*` en profiles).
 */
export default function AdminUsuariosPanel({ user: adminUser, onError }) {
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [zones, setZones] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [reports, setReports] = useState([]);
  const [ledgerSample, setLedgerSample] = useState([]);
  const [partnerRow, setPartnerRow] = useState(null);
  const loadReqId = useRef(0);

  const isSelfTarget = sel?.id && adminUser?.id && sel.id === adminUser.id;

  const load = useCallback(async () => {
    const reqId = ++loadReqId.current;
    setLoading(true);
    onError('');
    try {
      let query = supabase
        .from('profiles')
        .select(
          'id, username, email, created_at, is_admin, grooves, grooves_total, account_status, account_moderation_note, account_moderation_at'
        )
        .order('created_at', { ascending: false })
        .limit(200);

      if (statusFilter !== 'all') {
        query = query.eq('account_status', statusFilter);
      }

      const { data, error } = await query;
      if (reqId !== loadReqId.current) return;
      if (error) throw error;
      setRows(data || []);
    } catch (e) {
      if (reqId !== loadReqId.current) return;
      onError(e?.message || 'No se pudieron cargar usuarios');
      setRows([]);
    } finally {
      if (reqId === loadReqId.current) setLoading(false);
    }
  }, [onError, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter(
      r =>
        String(r.username || '')
          .toLowerCase()
          .includes(qq) || String(r.email || '').toLowerCase().includes(qq)
    );
  }, [rows, q]);

  const loadDetail = useCallback(
    async row => {
      if (!row?.id) return;
      setSel(row);
      setDetailLoading(true);
      setStats(null);
      setZones(null);
      setMsgs([]);
      setReports([]);
      setLedgerSample([]);
      setPartnerRow(null);
      onError('');
      const uid = row.id;
      try {
        const [
          listingsN,
          colN,
          comN,
          cgN,
          rOut,
          rIn,
          wlN,
          bidsN,
          buyN,
          sellN,
          folIn,
          folOut,
          notifN,
          megN,
          msgN,
        ] = await Promise.all([
          countExact('listings', 'seller_id', uid),
          countExact('profile_coleccion_items', 'user_id', uid),
          countExact('comunidad_publicaciones', 'author_id', uid),
          countExact('comunidad_guardados', 'user_id', uid),
          countExact('reviews', 'reviewer_id', uid),
          countExact('reviews', 'reviewed_id', uid),
          countExact('wishlists', 'user_id', uid),
          countExact('bids', 'bidder_id', uid),
          countExact('transactions', 'buyer_id', uid),
          countExact('transactions', 'seller_id', uid),
          countExact('follows', 'following_id', uid),
          countExact('follows', 'follower_id', uid),
          countExact('notifications', 'user_id', uid),
          countExact('noticia_megusta', 'user_id', uid),
          countExact('messages', 'sender_id', uid),
        ]);

        setStats({
          listings: listingsN,
          coleccion: colN,
          comunidad: comN,
          guardados_comunidad: cgN,
          reviews_out: rOut,
          reviews_in: rIn,
          wishlist: wlN,
          pujas: bidsN,
          compras: buyN,
          ventas: sellN,
          seguidores: folIn,
          siguiendo: folOut,
          notif: notifN,
          me_gusta_noticias: megN,
          mensajes: msgN,
        });

        const [{ data: zRows }, { data: mRows }, { data: repRows }, { data: glRows }, { data: pRows }] =
          await Promise.all([
            supabase.from('user_site_analytics').select('zone').eq('user_id', uid).order('created_at', { ascending: false }).limit(800),
            supabase.from('messages').select('id, content, created_at, conversation_id').eq('sender_id', uid).order('created_at', { ascending: false }).limit(35),
            supabase.from('user_reports').select('id, category, body, status, created_at, reporter_id').eq('target_user_id', uid).order('created_at', { ascending: false }).limit(50),
            supabase.from('grooves_ledger').select('delta, reason, created_at').eq('profile_id', uid).order('created_at', { ascending: false }).limit(20),
            supabase.from('partners').select('id, code, verification_status, grooves_partner').eq('profile_id', uid).maybeSingle(),
          ]);

        const zMap = {};
        (zRows || []).forEach(r => {
          const k = r.zone || '—';
          zMap[k] = (zMap[k] || 0) + 1;
        });
        setZones(zMap);
        setMsgs(mRows || []);
        setReports(repRows || []);
        setLedgerSample(glRows || []);
        setPartnerRow(pRows || null);
      } catch (e) {
        onError(e?.message || 'Error al cargar detalle');
      } finally {
        setDetailLoading(false);
      }
    },
    [onError]
  );

  const patchModeration = useCallback(
    async (row, nextStatus, opts = {}) => {
      if (!row?.id || busyId) return;
      const { notePrompt = true } = opts;
      let note = row.account_moderation_note || '';
      if (notePrompt) {
        const entered =
          window.prompt(
            'Nota visible para el usuario (opcional). Explica brevemente el motivo:',
            note || ''
          ) ?? null;
        if (entered === null) return;
        note = entered.trim();
      }
      setBusyId(row.id);
      onError('');
      try {
        const patch = {
          account_status: nextStatus,
          account_moderation_note: note || null,
          account_moderation_at: new Date().toISOString(),
          account_moderation_by: adminUser?.id || null,
        };
        const { error } = await supabase.from('profiles').update(patch).eq('id', row.id);
        if (error) throw error;
        setSel(s => (s?.id === row.id ? { ...s, ...patch } : s));
        setRows(prev => prev.map(r => (r.id === row.id ? { ...r, ...patch } : r)));
      } catch (e) {
        onError(e?.message || 'No se pudo actualizar');
      } finally {
        setBusyId(null);
      }
    },
    [adminUser?.id, busyId, onError]
  );

  const dismissReport = useCallback(
    async repId => {
      if (!repId || busyId === `rep:${repId}`) return;
      setBusyId(`rep:${repId}`);
      onError('');
      try {
        const { error } = await supabase
          .from('user_reports')
          .update({
            status: 'dismissed',
            reviewed_at: new Date().toISOString(),
            reviewed_by: adminUser?.id || null,
          })
          .eq('id', repId);
        if (error) throw error;
        if (sel) await loadDetail(sel);
      } catch (e) {
        onError(e?.message || 'No se pudo cerrar la denuncia');
      } finally {
        setBusyId(null);
      }
    },
    [adminUser?.id, busyId, loadDetail, onError, sel]
  );

  const markReportReviewed = useCallback(
    async repId => {
      if (!repId || busyId === `rev:${repId}`) return;
      setBusyId(`rev:${repId}`);
      onError('');
      try {
        const { error } = await supabase
          .from('user_reports')
          .update({
            status: 'reviewed',
            reviewed_at: new Date().toISOString(),
            reviewed_by: adminUser?.id || null,
          })
          .eq('id', repId);
        if (error) throw error;
        if (sel) await loadDetail(sel);
      } catch (e) {
        onError(e?.message || 'No se pudo marcar revisada');
      } finally {
        setBusyId(null);
      }
    },
    [adminUser?.id, busyId, loadDetail, onError, sel]
  );

  const openReports = useMemo(() => (reports || []).filter(r => r.status === 'open'), [reports]);

  return (
    <div>
      <DCard style={{ padding: '14px 16px', marginBottom: 14 }}>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.52)', margin: '0 0 12px', lineHeight: 1.55 }}>
          Resumen por usuario: actividad en marketplace, comunidad y valoraciones; zonas navegadas (desde cliente con
          muestra ligera); mensajes para revisión; denuncias. Estados de cuenta: <strong style={{ color: C.gold }}>activo</strong>,{' '}
          <strong style={{ color: '#EF9F27' }}>en revisión</strong> (pausa provisional) y{' '}
          <strong style={{ color: C.danger }}>bloqueado</strong>.
        </p>
        <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', margin: '0 0 12px', lineHeight: 1.45 }}>
          La eliminación total de cuenta (correo y auth.supabase) requiere acción desde consola DreamVinyl; desde aquí
          puedes bloquear el uso de la cuenta o pausarlo mientras analizás mensajes o denuncias.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Filtrar por usuario o correo…"
            style={{
              flex: '1 1 200px',
              minWidth: 160,
              padding: '8px 10px',
              borderRadius: 8,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.05)',
              color: '#fff',
              fontSize: 12,
            }}
          />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{
              padding: '8px 10px',
              borderRadius: 8,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: '#1a1a22',
              color: '#ccc',
              fontSize: 12,
            }}
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="paused_review">En revisión</option>
            <option value="blocked">Bloqueados</option>
          </select>
          <BtnGhost type="button" style={{ fontSize: 11, width: 'auto' }} onClick={() => load()}>
            Actualizar lista
          </BtnGhost>
        </div>
      </DCard>

      <div style={{ display: 'grid', gridTemplateColumns: sel ? 'minmax(0,1fr) minmax(320px,1fr)' : '1fr', gap: 14, alignItems: 'start' }}>
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 20, fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Cargando…</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 640 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <th style={{ padding: '10px 12px' }}>Usuario</th>
                    <th style={{ padding: '10px 12px' }}>Correo</th>
                    <th style={{ padding: '10px 12px' }}>Grooves</th>
                    <th style={{ padding: '10px 12px' }}>Estado</th>
                    <th style={{ padding: '10px 12px' }} />
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(r => (
                    <tr
                      key={r.id}
                      style={{
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        color: sel?.id === r.id ? C.gold : '#eaeaea',
                        background: sel?.id === r.id ? 'rgba(200,168,75,0.06)' : 'transparent',
                      }}
                    >
                      <td style={{ padding: '10px 12px', fontWeight: 650 }}>
                        @{r.username}
                        {r.is_admin ? (
                          <span style={{ marginLeft: 6, fontSize: 9, color: '#5DCAA5' }}>admin</span>
                        ) : null}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
                        {r.email || '—'}
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        <span title="Total acumulado">{profileGroovesBalance(r)}</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: 10 }}>
                          {r.account_status === 'blocked' ? (
                            <span style={{ color: C.danger }}>Bloqueado</span>
                          ) : r.account_status === 'paused_review' ? (
                            <span style={{ color: '#EF9F27' }}>Revisión</span>
                          ) : (
                            <span style={{ color: 'rgba(255,255,255,0.45)' }}>Activo</span>
                          )}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <button type="button" onClick={() => loadDetail(r)} style={{ ...btnMini, width: 'auto' }}>
                          Ver ficha
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!visibleRows.length ? (
                <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin resultados.</div>
              ) : null}
            </div>
          )}
        </DCard>

        {sel ? (
          <DCard style={{ padding: '14px 16px', position: 'sticky', top: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 750, color: '#fff', marginBottom: 4 }}>
                  @{sel.username}{' '}
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontWeight: 400 }}>
                    Alta {formatFecha(sel.created_at)}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', wordBreak: 'break-all' }}>{sel.email}</div>
                {partnerRow ? (
                  <div style={{ marginTop: 10, fontSize: 11, color: C.gold }}>
                    Partner{' '}
                    <strong>{partnerRow.code}</strong> · {partnerRow.verification_status}
                    {partnerRow.grooves_partner != null ? ` · grooves partner ${partnerRow.grooves_partner}` : null}
                  </div>
                ) : null}
              </div>
              <button type="button" onClick={() => setSel(null)} style={{ ...btnMini, borderColor: 'rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.6)' }}>
                Cerrar
              </button>
            </div>

            {detailLoading ? (
              <div style={{ marginTop: 16, color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>Cargando métricas…</div>
            ) : stats ? (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 10, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>VOLÚMENES</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 8 }}>
                  {STAT_LABELS.map(([key, lbl]) => (
                    <div
                      key={key}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 8,
                        background: 'rgba(255,255,255,0.04)',
                        border: '0.5px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>{lbl}</div>
                      <div style={{ fontSize: 16, fontWeight: 750, color: '#fff', marginTop: 2 }}>
                        {stats[key] == null ? '—' : stats[key]}
                      </div>
                    </div>
                  ))}
                </div>

                {zones && Object.keys(zones).length ? (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 10, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>
                      ZONAS VISITADAS (últ. muestras, cliente)
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {Object.entries(zones)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 24)
                        .map(([z, n]) => (
                          <span
                            key={z}
                            style={{
                              fontSize: 10,
                              padding: '4px 8px',
                              borderRadius: 20,
                              background: 'rgba(200,168,75,0.12)',
                              border: '0.5px solid rgba(200,168,75,0.28)',
                              color: C.gold,
                            }}
                          >
                            {z} · {n}
                          </span>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 14, fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                    Sin datos de zonas (o usuario sin actividad reciente tras desplegar la migración de analíticas).
                  </div>
                )}

                {ledgerSample.length ? (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 10, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>
                      Últimos grooves (ledger)
                    </div>
                    <div style={{ maxHeight: 140, overflow: 'auto', fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>
                      {ledgerSample.map((g, i) => (
                        <div key={`${g.created_at}-${i}`} style={{ padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <span style={{ color: g.delta >= 0 ? C.green : C.danger }}>{g.delta >= 0 ? '+' : ''}{g.delta}</span>{' '}
                          {g.reason} · {formatFecha(g.created_at)}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 10, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>
                    DENUNCIAS {openReports.length ? `(${openReports.length} abiertas)` : ''}
                  </div>
                  {!reports.length ? (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Sin denuncias registradas.</div>
                  ) : (
                    <div style={{ maxHeight: 160, overflow: 'auto' }}>
                      {reports.map(rep => (
                        <div
                          key={rep.id}
                          style={{
                            padding: '8px 10px',
                            marginBottom: 6,
                            borderRadius: 8,
                            background: 'rgba(255,255,255,0.04)',
                            fontSize: 10,
                          }}
                        >
                          <div style={{ color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
                            {rep.category} · {formatFecha(rep.created_at)} · estado:{' '}
                            <strong style={{ color: rep.status === 'open' ? C.gold : 'rgba(255,255,255,0.5)' }}>
                              {rep.status}
                            </strong>
                          </div>
                          {rep.body ? <div style={{ color: '#ddd', whiteSpace: 'pre-wrap' }}>{rep.body}</div> : null}
                          {rep.status === 'open' ? (
                            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                disabled={!!busyId}
                                onClick={() => markReportReviewed(rep.id)}
                                style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                              >
                                Revisada
                              </button>
                              <button type="button" disabled={!!busyId} onClick={() => dismissReport(rep.id)} style={btnMini}>
                                Descartar
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 10, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>
                    MENSAJES (recientes enviados)
                  </div>
                  {!msgs.length ? (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Sin mensajes en base o sin permiso legacy.</div>
                  ) : (
                    <div style={{ maxHeight: 200, overflow: 'auto' }}>
                      {msgs.map(m => (
                        <div
                          key={m.id}
                          style={{
                            padding: '6px 8px',
                            marginBottom: 4,
                            borderRadius: 6,
                            background: 'rgba(0,0,0,0.25)',
                            fontSize: 10,
                            color: 'rgba(255,255,255,0.8)',
                          }}
                        >
                          <div style={{ color: 'rgba(255,255,255,0.35)', marginBottom: 4 }}>
                            {formatFecha(m.created_at)}{' '}
                            <span style={{ fontFamily: 'monospace', opacity: 0.6 }}>
                              conv {String(m.conversation_id || '').slice(0, 8)}…
                            </span>
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.content}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ fontSize: 10, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', marginBottom: 10 }}>
                    MODERACIÓN CUENTA
                  </div>
                  {sel.account_moderation_note ? (
                    <DCard style={{ padding: 10, marginBottom: 10, fontSize: 11, background: 'rgba(0,0,0,0.2)', color: 'rgba(255,255,255,0.75)' }}>
                      <strong style={{ color: C.gold }}>Nota activa para el usuario</strong>
                      <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{sel.account_moderation_note}</div>
                    </DCard>
                  ) : null}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <button
                      type="button"
                      disabled={!!busyId || isSelfTarget}
                      title={isSelfTarget ? 'No podés aplicarte moderación desde tu propia sesión así.' : ''}
                      onClick={() => patchModeration(sel, 'active', { notePrompt: true })}
                      style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                    >
                      Reactivar
                    </button>
                    <button
                      type="button"
                      disabled={!!busyId || isSelfTarget || sel.is_admin}
                      title={sel.is_admin ? 'No pongas admins en revisión desde aquí (coordinación interna).' : ''}
                      onClick={() =>
                        patchModeration(sel, 'paused_review', {
                          notePrompt: true,
                        })
                      }
                      style={{ ...btnMini, borderColor: 'rgba(239,159,39,0.45)', color: '#EF9F27' }}
                    >
                      Pausar (revisión)
                    </button>
                    <button
                      type="button"
                      disabled={!!busyId || isSelfTarget || sel.is_admin}
                      title={sel.is_admin ? '' : ''}
                      onClick={() => {
                        if (
                          !window.confirm(
                            '¿BLOQUEAR la cuenta? El usuario verá pantalla en la app hasta reactivación. ¿Continuar?'
                          )
                        )
                          return;
                        patchModeration(sel, 'blocked', { notePrompt: true });
                      }}
                      style={{ ...btnMini, borderColor: 'rgba(226,75,74,0.45)', color: C.danger }}
                    >
                      Bloquear cuenta
                    </button>
                  </div>
                  {isSelfTarget ? (
                    <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 10 }}>
                      Esta fila sos vos: usá otro admin para moderar tu perfil si hiciera falta.
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </DCard>
        ) : null}
      </div>
    </div>
  );
}
